import React, { FC, useState } from 'react';
import { Select } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { FieldValueMapping } from './ImportGridTypes';

interface FieldValueMapperProps {
	fieldValueMappings: FieldValueMapping[];
	onConfirm: (mappings: FieldValueMapping[]) => void;
}

const thClassName = 'text-left px-3 py-2 border-b-2 border-[#eee] font-semibold';
const tdClassName = 'px-3 py-1.5 border-b border-grey-4';

const FieldValueMapper: FC<FieldValueMapperProps> = ({ fieldValueMappings, onConfirm }) => {
	const [localMappings, setLocalMappings] = useState<FieldValueMapping[]>(fieldValueMappings);

	const handleChange = (fieldIndex: number, entryIndex: number, value: string | number | null) => {
		const updated = localMappings.map((fieldMapping, currentFieldIndex) => {
			if (currentFieldIndex !== fieldIndex) return fieldMapping;
			const updatedEntries = fieldMapping.entries.map((entry, currentEntryIndex) => {
				if (currentEntryIndex !== entryIndex) return entry;
				return {
					csvValue: entry.csvValue,
					mappedValue: value === undefined ? null : value,
					autoMatched: entry.autoMatched,
					count: entry.count,
				};
			});
			return {
				fieldKey: fieldMapping.fieldKey,
				fieldLabel: fieldMapping.fieldLabel,
				options: fieldMapping.options,
				entries: updatedEntries,
			};
		});
		setLocalMappings(updated);
	};

	const handleConfirm = () => {
		onConfirm(localMappings);
	};

	return (
		<div>
			<div className="mb-4 text-[#666]">
				Verify the mapped values below. You can change any mapping or leave values unmapped.
			</div>
			<div className="max-h-96 overflow-y-auto">
				{localMappings.map((fieldMapping, fieldIndex) => (
					<div key={fieldMapping.fieldKey} className="mb-6">
						<div className="bodymedium mb-2">{fieldMapping.fieldLabel}</div>
						<table className="w-full border-collapse">
							<thead>
								<tr>
									<th className={thClassName}>CSV Value</th>
									<th className={thClassName}>Maps To</th>
									<th className={thClassName}>Rows</th>
								</tr>
							</thead>
							<tbody>
								{fieldMapping.entries.map((entry, entryIndex) => (
									<tr key={entry.csvValue}>
										<td className={tdClassName}>{entry.csvValue}</td>
										<td className={tdClassName}>
											<div className="flex items-center gap-1.5">
												{entry.autoMatched && entry.mappedValue !== null && (
													<CheckCircleOutlined className="shrink-0 text-[#52c41a]" />
												)}
												<Select
													value={entry.mappedValue !== null ? entry.mappedValue : undefined}
													onChange={(val) =>
														handleChange(fieldIndex, entryIndex, val !== undefined ? val : null)
													}
													placeholder="-- Leave unmapped --"
													allowClear
													showSearch
													filterOption={(input, option) =>
														String((option && option.children) || '')
															.toLowerCase()
															.includes(input.toLowerCase())
													}
													className="w-full"
													dropdownStyle={{ zIndex: 2000 }}
												>
													{fieldMapping.options.map((option) => (
														<Select.Option key={String(option.value)} value={option.value}>
															{option.label}
														</Select.Option>
													))}
												</Select>
											</div>
										</td>
										<td className={tdClassName}>
											<span className="footnoteregular rounded-[10px] bg-grey-4 px-2 py-0.5">
												{entry.count}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				))}
			</div>
			<div className="mt-4 text-right">
				<button
					onClick={handleConfirm}
					className="cursor-pointer rounded border-none bg-[#1890ff] px-5 py-1.5 text-white"
				>
					Confirm Value Mapping
				</button>
			</div>
		</div>
	);
};

export default FieldValueMapper;
